import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { Loader2, Save, RotateCcw, ExternalLink, Scale, Eye, PencilLine } from "lucide-react";
import AdminLayout, { adminHeaders, useAdminAuthed } from "@/components/AdminLayout";
import { LEGAL_LINKS } from "@/pages/LegalPage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminLegalPagesPage() {
  const pw = useAdminAuthed();
  const [active, setActive] = useState(LEGAL_LINKS[0].slug);
  const [page, setPage] = useState(null);
  const [draft, setDraft] = useState({ title: "", eyebrow: "", body: "" });
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);

  const load = useCallback(async (slug) => {
    setPage(null);
    try {
      const { data } = await axios.get(`${API}/legal/${slug}`);
      setPage(data);
      setDraft({ title: data.title || "", eyebrow: data.eyebrow || "", body: data.body || "" });
    } catch {
      toast.error("Failed to load page");
    }
  }, []);

  useEffect(() => { if (pw) load(active); }, [pw, active, load]);

  const dirty = page && (draft.title !== page.title || draft.eyebrow !== (page.eyebrow || "") || draft.body !== page.body);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/admin/legal/${active}`, draft, { headers: adminHeaders() });
      setPage(data);
      toast.success("Page saved — live on the website");
    } catch (e) {
      toast.error(e?.response?.data?.detail?.[0]?.msg || e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const reset = async () => {
    if (!window.confirm("Restore the default HerbKranti text for this page? Your edits will be lost.")) return;
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/admin/legal/${active}/reset`, {}, { headers: adminHeaders() });
      setPage(data);
      setDraft({ title: data.title, eyebrow: data.eyebrow || "", body: data.body });
      toast.success("Default content restored");
    } catch {
      toast.error("Reset failed");
    } finally { setSaving(false); }
  };

  const link = LEGAL_LINKS.find((l) => l.slug === active);

  return (
    <AdminLayout title="Legal Pages" subtitle="Terms, Privacy, Refund & Cancellation, Shipping — edit the live text directly">
      <div className="space-y-6" data-testid="admin-legal-page">
        <div className="flex flex-wrap gap-2" data-testid="legal-tabs">
          {LEGAL_LINKS.map((l) => (
            <button key={l.slug} onClick={() => setActive(l.slug)}
              className={"px-4 py-2 rounded-full text-[12px] tracking-[0.12em] uppercase border transition-colors " +
                (active === l.slug ? "bg-[var(--hk-ink)] text-white border-[var(--hk-ink)]" : "bg-white border-[var(--hk-border)] text-[var(--hk-ink-2)] hover:border-[var(--hk-ink)]")}
              data-testid={`legal-tab-${l.slug}`}>
              {l.label}
            </button>
          ))}
        </div>

        <div className="hk-card p-6 space-y-5" data-testid="legal-editor-card">
          {!page ? (
            <div className="py-10 text-center text-[var(--hk-ink-3)]"><Loader2 className="animate-spin inline" size={20} /></div>
          ) : (
            <>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Scale size={18} className="text-[var(--hk-green,#3a7d44)]" />
                  <h2 className="text-lg font-semibold">{link.label}</h2>
                  <a href={link.href} target="_blank" rel="noreferrer" className="text-xs inline-flex items-center gap-1 text-[var(--hk-ink-3)] hover:text-[var(--hk-orange-deep)]" data-testid="legal-view-live">
                    View live <ExternalLink size={12} />
                  </a>
                </div>
                <div className="text-[11px] text-[var(--hk-ink-3)]" data-testid="legal-updated-at">
                  {page.updated_at ? `Last saved ${new Date(page.updated_at).toLocaleString()}` : "Default content"}
                </div>
              </div>

              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Page title</label>
                  <input className="hk-input h-10 w-full" value={draft.title}
                    onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))} data-testid="legal-title-input" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-[var(--hk-ink-3)] mb-1">Small label above title</label>
                  <input className="hk-input h-10 w-full" value={draft.eyebrow} placeholder="Legal · Terms of Use"
                    onChange={(e) => setDraft((d) => ({ ...d, eyebrow: e.target.value }))} data-testid="legal-eyebrow-input" />
                </div>
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-xs font-medium text-[var(--hk-ink-3)]">Content (Markdown)</label>
                  <button type="button" onClick={() => setPreview((p) => !p)}
                    className="text-xs inline-flex items-center gap-1 text-[var(--hk-ink-3)] hover:text-[var(--hk-ink)]" data-testid="legal-preview-toggle">
                    {preview ? <><PencilLine size={12} /> Edit</> : <><Eye size={12} /> Preview</>}
                  </button>
                </div>
                {preview ? (
                  <div className="rounded-md border border-[var(--hk-border)] bg-white p-5 max-h-[60vh] overflow-auto prose-sm [&_h2]:font-semibold [&_h2]:text-base [&_h2]:mt-5 [&_p]:mt-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mt-2 [&_blockquote]:border-l-4 [&_blockquote]:pl-3 [&_blockquote]:italic" data-testid="legal-preview">
                    <ReactMarkdown>{draft.body}</ReactMarkdown>
                  </div>
                ) : (
                  <textarea className="hk-input w-full min-h-[60vh] font-mono text-[13px] leading-relaxed" value={draft.body}
                    onChange={(e) => setDraft((d) => ({ ...d, body: e.target.value }))} data-testid="legal-body-input" />
                )}
                <p className="mt-1.5 text-[11px] text-[var(--hk-ink-3)]">
                  Formatting: <code>## Heading</code> · <code>**bold**</code> · <code>- bullet</code> · <code>&gt; highlighted note</code> · blank line = new paragraph
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-3 pt-1">
                <button onClick={save} disabled={saving || !dirty} className="hk-btn-primary h-10 px-5 inline-flex items-center gap-2 disabled:opacity-50" data-testid="legal-save-btn">
                  {saving ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} Save & publish
                </button>
                <button onClick={reset} disabled={saving} className="hk-btn-ghost h-10 px-4 inline-flex items-center gap-2 text-xs" data-testid="legal-reset-btn">
                  <RotateCcw size={13} /> Restore default text
                </button>
                {dirty && <span className="text-xs text-[var(--hk-orange-deep)]" data-testid="legal-unsaved">Unsaved changes</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
